import { useTheme } from "@mui/material";
import { NodeProps } from "reactflow";
import StandardNode from "components/Flow/Nodes/StandardNode";
import StandardNodeHeader from "components/Flow/Nodes/StandardNode/StandardNodeHeader";
import StandardNodeFunctionality from "components/Flow/Nodes/StandardNode/StandardNodeFunctionality";
import StandardNodeFooter from "components/Flow/Nodes/StandardNode/StandardNodeFooter";

import { functionalityValuesAPI, communityNodeAPI } from "api/Community/CommunityAPI";

export interface CommunityPathwayNodeData {
  name: string;
  category?: string;
  type?: string;
  colorPrimary: string;

  linkedNode?: boolean;
  baseLink?: string;
  linkedId?: string | number;

  functionalities?: any[];
  people?: any[];
  peopleCount?: number;
  addresses?: any[];
  addressesCount?: number;
  itemTypes?: "People" | "Addresses" | "Both";

  updateNode: (id: string, field: string, value: any) => void;
  updateNodeFunctionality: (
    nodeId: string,
    functionalityId: number,
    field: string,
    value: any,
    valueId?: number | null
  ) => void;
}

const CommunityPathwayNode = ({
  id,
  data,
  selected,
}: NodeProps<CommunityPathwayNodeData>) => {
  const theme = useTheme();

  const handleFunctionalityUpdateField = async (
    functionalityId: number,
    valueId: number | null,
    field: string,
    value: any
  ) => {
    let newValueId = valueId;

    if (!valueId) {
      const created = await functionalityValuesAPI.create({
        node: Number(id),
        functionality_type: Number(functionalityId),
        value: value,
      });

      newValueId = created.id;
    } else {
      await functionalityValuesAPI.updateField(Number(valueId), field, value);
    }

    data.updateNodeFunctionality(
      id,
      functionalityId,
      field,
      value,
      newValueId
    );
  };

  const handleNodeUpdateField = async (
    nodeId: number | string,
    field: string,
    value: any
  ) => {
    await communityNodeAPI.updateField(Number(nodeId), field, value);
    data.updateNode(String(id), field, value);
  };

  return (
    <StandardNode
      selected={selected}
      borderColor={data.colorPrimary}
      backgroundColor={theme.custom.nodes.background.main.dark}
      header={
        <StandardNodeHeader
          title={data.name}
          subtitle={data.category}
          secondaryText={data.type}
          backgroundColor={data.colorPrimary}
          textColor={theme.custom.nodes.text.primary.dark}
          secondaryTextColor={theme.custom.nodes.text.secondary.dark}
          linkUrl={
            data.linkedNode && data.baseLink && data.linkedId
              ? `${data.baseLink}${data.linkedId}`
              : undefined
          }
          apiNodeUpdateFieldFn={handleNodeUpdateField}
          nodeId={id}
        />
      }
      functionalities={
        <StandardNodeFunctionality
          functionalities={data.functionalities || []}
          apiFunctionalityUpdateFieldFn={handleFunctionalityUpdateField}
          nodeId={Number(id)}
          nodeAPI={communityNodeAPI}
          currentPeople={data.people ?? []}
          currentAddresses={data.addresses ?? []}
        />
      }
      footer={
        <StandardNodeFooter
          centerSection={{
            value: data.peopleCount ?? 0,
            label: "People",
          }}
        />
      }
    />
  );
};

export default CommunityPathwayNode;